import { useRef, useState } from "react";
import { Paperclip, X, Loader2, Film, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function AttachmentUploader({ value = [], onChange }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (value.length + files.length > 10) {
      toast.error("10 fichiers maximum par message");
      return;
    }
    setUploading(true);
    const newAtts = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/ticket-attachment", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newAtts.push(data);
      } catch (err) {
        toast.error(`${file.name} : ${err?.response?.data?.detail || "Erreur"}`);
      }
    }
    if (newAtts.length > 0) {
      onChange([...value, ...newAtts]);
      toast.success(`${newAtts.length} fichier(s) ajouté(s)`);
    }
    setUploading(false);
    e.target.value = "";
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
        multiple
        onChange={handleFiles}
        className="hidden"
        data-testid="ticket-attachment-input"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={pick}
        disabled={uploading}
        data-testid="ticket-attachment-button"
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Paperclip className="h-3.5 w-3.5 mr-1" />}
        {uploading ? "Envoi…" : "Ajouter photo / vidéo"}
      </Button>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((a, i) => (
            <div key={i} className="relative group">
              {a.is_video ? (
                <div className="w-24 h-24 rounded bg-secondary flex flex-col items-center justify-center text-xs">
                  <Film className="h-5 w-5 text-primary" />
                  <span className="mt-1">Vidéo</span>
                </div>
              ) : (
                <img src={a.url} alt="" className="w-24 h-24 object-cover rounded" />
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Retirer"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">Images (PNG, JPG, WEBP, GIF) et vidéos courtes (MP4, WEBM, MOV) — 25 MB max par fichier, 10 fichiers max.</p>
    </div>
  );
}

export function AttachmentGallery({ attachments = [] }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {attachments.map((a, i) => {
        const url = typeof a === "string" ? a : a.url;
        const isVideo = typeof a === "object" ? a.is_video : /\.(mp4|webm|mov)(\?|$)/i.test(url);
        return isVideo ? (
          <video key={i} src={url} controls className="max-w-xs max-h-64 rounded border border-border" />
        ) : (
          <a key={i} href={url} target="_blank" rel="noreferrer">
            <img src={url} alt="Pièce jointe" className="max-w-xs max-h-64 rounded border border-border hover:border-primary transition-colors" />
          </a>
        );
      })}
    </div>
  );
}
